// Renders an assistant message as Markdown, mirroring the desktop MessageBubble
// (assistant prose is Markdown; fenced code is a code block with a language
// badge + copy button). User messages are plain text and do NOT use this.
// Uses react-markdown (no innerHTML) so participant-facing content stays safe.

import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable; ignore */
    }
  };
  return (
    <div className="jol-codeblock">
      <div className="jol-codeblock-bar">
        <span className="jol-codeblock-lang">{language || "code"}</span>
        <button type="button" className="jol-codeblock-copy" onClick={onCopy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function AssistantMarkdown({ text }: { text: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // react-markdown v9 removed the `inline` prop. Distinguish a fenced
        // block (has a language class OR spans multiple lines) from inline code.
        code({ className, children, ...props }: any) {
          const raw = String(children ?? "").replace(/\n$/, "");
          const match = /language-(\w+)/.exec(className || "");
          const isBlock = !!match || raw.includes("\n");
          if (!isBlock) {
            return (
              <code className="jol-inline-code" {...props}>
                {children}
              </code>
            );
          }
          return <CodeBlock language={match ? match[1] : ""} code={raw} />;
        },
        // Unwrap the default <pre> so our CodeBlock isn't nested inside one.
        pre({ children }: { children?: ReactNode }) {
          return <>{children}</>;
        },
        a({ children, href }: any) {
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );
}
